<template>
  <v-container fluid class="pa-6">
    <!-- Header -->
    <div class="d-flex justify-space-between align-center mb-6">
      <h1 class="text-h5 font-weight-bold text-grey-darken-4">Lista de Usuários</h1>
      <v-btn color="primary" @click="handleCreateUser">
        <v-icon start>mdi-plus</v-icon>
        Adicionar Novo Usuário
      </v-btn>
    </div>

    <!-- Tabela -->
    <v-card class="elevation-4">
      <v-card-text>
        <!-- Filtro -->
        <v-row class="mb-6" dense>
          <v-col cols="12" md="8">
            <v-text-field
              v-model="searchTerm"
              placeholder="Pesquisar usuário"
              clearable
              hide-details
              prepend-inner-icon="mdi-magnify"
            />
          </v-col>
        </v-row>

        <!-- Data Table -->
        <v-data-table
          :headers="columns"
          :items="filteredUsers"
          :items-per-page="itemsPerPage"
          :page.sync="page"
          :loading="isLoading"
          :no-data-text="noDataMessage"
          class="elevation-0"
        >
          <template #item.name="{ item }">
            <span>{{ item.name }}</span>
          </template>

          <template #item.email="{ item }">
            <span>{{ item.email }}</span>
          </template>

          <template #item.phone="{ item }">
            <span>{{ formatPhone(item.phone) }}</span>
          </template>

          <template #item.role="{ item }">
              {{ formatRole(item.role) }}
          </template>

          <template #item.is_active="{ item }">
            <v-chip :color="isActiveTagMap[item.is_active]" label>
              {{ formatIsActive(item.is_active) }}
            </v-chip>
          </template>

          <template #item.actions="{ item }">
            <v-icon
              icon="mdi-pencil"
              color="warning"
              class="mr-2"
              @click="handleEdit(item)"
              title="Editar"
            />
            <v-icon
              icon="mdi-lock-reset"
              color="info"
              @click="handleResetPassword(item)"
              title="Trocar Senha"
            />
          </template>
        </v-data-table>

        <!-- Paginação -->
        <div class="d-flex justify-center mt-4">
          <v-pagination
            v-model="page"
            :length="pageCount"
            rounded="circle"
            color="primary"
          />
        </div>

        <!-- Erro -->
        <v-alert
          v-if="hasError && !isLoading"
          type="error"
          variant="tonal"
          class="mt-4 text-center"
        >
          Ocorreu um erro ao carregar os dados.
        </v-alert>
      </v-card-text>
    </v-card>

    <!-- Modal de Reset de Senha -->
    <v-dialog v-model="showPasswordModal" max-width="500px">
      <v-card>
        <v-card-title class="headline text-h6 font-weight-bold text-grey-darken-4">
          Trocar Senha
        </v-card-title>
        <v-card-text>
          <v-form ref="passwordForm">
            <v-text-field
              v-model="newPassword"
              label="Nova Senha"
              type="password"
              :rules="[rules.required, rules.min]"
              required
            />
            <v-text-field
              v-model="confirmPassword"
              label="Confirmar Nova Senha"
              type="password"
              :rules="[rules.required, passwordMatch]"
              required
            />
          </v-form>
        </v-card-text>
        <v-card-actions class="d-flex justify-space-between">
          <v-btn color="error" variant="text" @click="closePasswordModal">
            Cancelar
          </v-btn>
          <v-btn color="primary" variant="flat" @click="resetPassword">
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import api from "@/services/api";
import { useToastStore } from "@/store/toast";
import { useLoadingOverlay } from "@/store/loadingOverlay";

const router = useRouter();
const toastStore = useToastStore();
const loadingOverlay = useLoadingOverlay();
const user = ref({});

const users = ref([]);
const isLoading = ref(true);
const hasError = ref(false);
const searchTerm = ref("");

// paginação
const page = ref(1);
const itemsPerPage = 10;

const showPasswordModal = ref(false);
const newPassword = ref("");
const confirmPassword = ref("");
const selectedUser = ref(null);
const passwordForm = ref(null);

const passwordMatch = computed(() => {
  return newPassword.value === confirmPassword.value || "As senhas não coincidem.";
});

const rules = {
  required: (value) => !!value || "Campo obrigatório.",
  min: (v) => v.length >= 6 || "A senha deve ter no mínimo 6 caracteres.",
};

const isActiveTagMap = {
  0: "error",
  1: "success",
};

const filteredUsers = computed(() => {
  let result = users.value;

  if (searchTerm.value) {
    const term = searchTerm.value.toLowerCase();
    result = result.filter((user) => user.name.toLowerCase().includes(term));
  }

  return result;
});

const noDataMessage = computed(() => {
  if (isLoading.value) return "Carregando...";
  if (filteredUsers.value.length === 0) return "Nenhum usuário encontrado.";
  return "";
});

const pageCount = computed(() =>
  Math.ceil(filteredUsers.value.length / itemsPerPage)
);

const paginatedUsers = computed(() => {
  const start = (page.value - 1) * itemsPerPage;
  const end = start + itemsPerPage;
  return filteredUsers.value.slice(start, end);
});

const columns = [
  { title: "Nome", key: "name", sortable: true },
  { title: "Telefone", key: "phone", sortable: true },
  { title: "Perfil", key: "role", sortable: true },
  { title: "Situação", key: "is_active", sortable: true },
  { title: "Ações", key: "actions", sortable: false },
];

const loadUsers = async () => {
  isLoading.value = true;
  hasError.value = false;
  loadingOverlay.startLoading();
  try {
    const response = await api.get("/users", {
      params: {
        email: user.value.email
      }
    });
    users.value = response.data.data;
  } catch (error) {
    console.error("Erro ao carregar usuários:", error);
    hasError.value = true;
    toastStore.showToast({
      message:
        error?.response?.data?.error ||
        "Ocorreu um erro ao carregar a lista de usuários.",
      color: "error",
    });
  } finally {
    isLoading.value = false;
    loadingOverlay.stopLoading();
  }
};

const handleCreateUser = () => {
  router.push("/app/usuarios/novo");
};

const handleEdit = (row) => {
  router.push({ name: "user-edit", params: { id: row.id } });
};

const handleResetPassword = (item) => {
  selectedUser.value = item;
  showPasswordModal.value = true;
};

const closePasswordModal = () => {
  showPasswordModal.value = false;
  newPassword.value = "";
  confirmPassword.value = "";
  selectedUser.value = null;
  passwordForm.value.resetValidation();
};

const resetPassword = async () => {
  const { valid } = await passwordForm.value.validate();
  if (!valid) return;

  loadingOverlay.startLoading();
  try {
    const response = await api.put(`/users/${selectedUser.value.id}/password`, {
      password: newPassword.value,
    });

    if (response.status === 200) {
      toastStore.showToast({
        message: "Senha alterada com sucesso!",
        color: "success",
      });
      closePasswordModal();
    } else {
      toastStore.showToast({
        message: "Ocorreu um erro ao tentar alterar a senha.",
        color: "error",
      });
    }
  } catch (error) {
    console.error("Erro ao trocar a senha:", error);
    toastStore.showToast({
      message:
        error?.response?.data?.error || "Ocorreu um erro ao trocar a senha.",
      color: "error",
    });
  } finally {
    loadingOverlay.stopLoading();
  }
};

function formatIsActive(is_active) {
  switch (is_active) {
    case 0:
      return "Desativado";
    case 1:
      return "Ativo";
    default:
      return is_active;
  }
}

function formatRole(role) {
  switch (role) {
    case 'broker':
      return "Corretor";
    case 'leadbroker':
      return "Corretor Lider";
    case 'adminbroker':
      return "Corretor Administrador";
    case 'administrative':
      return "Administrativo";
    case 'client':
      return "Cliente";
    case 'admin':
      return "Administrador";
    default:
      return role;
  }
}

function formatPhone(phone) {
  if (!phone) return "";

  const cleaned = ("" + phone).replace(/\D/g, "");

  const match11 = cleaned.match(/^(\d{2})(\d{5})(\d{4})$/);
  if (match11) return `(${match11[1]}) ${match11[2]}-${match11[3]}`;

  const match10 = cleaned.match(/^(\d{2})(\d{4})(\d{4})$/);
  if (match10) return `(${match10[1]}) ${match10[2]}-${match10[3]}`;

  return phone;
}

onMounted(() => {
  const userDataString = localStorage.getItem('userData');
  const userData = JSON.parse(userDataString);
  if(userData.user){
    user.value = userData.user;
  }
  loadUsers();
});
</script>
